import * as React from "react";
import { Box, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { JSX, useState } from "react";
import Can from "../Policy/Can";
import Filters from "./Filters";
import * as reportsApi from "../../api/reports";
import { rupeeDisplay } from "../../helpers/rupee";
import { tl } from "../../components/translate";
import { notificationAdd } from "../../actions/notification";
import useIsReactNativeWebView from "../../hooks/useIsReactNativeWebView";
import useMobileScreen from "../../hooks/useMobileScreen";
import * as calFns from "../../components/Calendar/functions/calendarFunctions";
import List, { EmptyView } from "../../components/OList";

// @ts-expect-error - css
import styles from "./Reports.module.css";
import "./Reports.scss";
import { BillingPackage } from "../../interfaces/BillingPackages";
import { clientFullNameSelector } from "../../reducers/client";
import BillingPackageStatusChip from "../Billing/BillingPackages/BillingPackageStatusChip";

const or = (val) => val || "";

const processData = (data: BillingPackage[]) =>
  data.map((item) => {
    const { document } = item;

    const sections = [
      ...(document?.packaged || []),
      ...(document?.nonPackaged || []),
      ...(document?.additional || [])
    ];

    const totals = sections.reduce(
      (sum, curr) => {
        const discount = Number(curr?.discountAmt || 0);
        const paid = Number(curr?.paidAmount || 0);
        const due = Number(curr?.dueAmount || 0);
        return {
          discountAmount: sum.discountAmount + discount,
          paidAmount: sum.paidAmount + paid,
          dueAmount: sum.dueAmount + due
        };
      },
      {
        discountAmount: 0,
        paidAmount: 0,
        dueAmount: 0
      }
    );

    return {
      ...item,
      ...totals
    };
  });

const BillingPackageReport = (): JSX.Element => {
  const dispatch = useDispatch();
  const isMobileScreen = useMobileScreen();
  const { isRnWebView } = useIsReactNativeWebView();
  const [isRequestLoading, setIsRequestLoading] = useState(false);

  // local states
  const [reportData, setReportData] = React.useState<BillingPackage[]>([]);

  const [filters, setFilters] = React.useState({
    from: calFns.startOfDay(new Date()).toDate(),
    until: new Date(),
    clientIds: []
  });

  // helpers
  const applyFilters = (newFilters) => {
    setFilters(newFilters);
  };

  // effect hooks
  React.useEffect(() => {
    setIsRequestLoading(true);
    reportsApi
      .billingPackageReports(filters)
      .then((data) => {
        setReportData(processData(data));
      })
      .catch((err: any) => {
        dispatch(
          notificationAdd({
            id: new Date().getUTCMilliseconds(),
            variant: "error",
            message: err?.data?.message || "Failed to fetch package report.",
            autoTimeout: true
          })
        );
      })
      .finally(() => setIsRequestLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return (
    <Can policyAccessKey="report:salesReport">
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* filter */}
        <Box sx={{ px: 3, py: 2 }}>
          <Filters
            filters={filters}
            onSetFilters={(newFilters) => applyFilters(newFilters)}
            isRequestLoading={isRequestLoading}
          />
        </Box>

        {/* list */}
        <Box
          className={["billingPackageReportList", styles.salesSummaryListStyles].join(" ")}
          sx={{
            borderTop: 1,
            borderColor: "divider",
            width: "100%",
            overflowX: "auto",
            marginBottom: "40px"
          }}
        >
          <Box sx={{ minWidth: isMobileScreen || isRnWebView ? "1100px" : "auto" }}>
            <List
              automation="billingPackageReportList"
              data={reportData}
              rowHeight={50}
              defaultSortColumn="createdDate"
              defaultSortOrder={-1}
              activeRow={1}
              adjustHeightToContents
              columns={[
                {
                  key: "createdDate",
                  label: "Created Date",
                  // eslint-disable-next-line camelcase
                  formatter: ({ created_at }) => (
                    <Typography>{calFns.bsFullDate(created_at)}</Typography>
                  ),
                  sortable: true,
                  sortFunction: (a, b) => (new Date(a.created_at) > new Date(b.created_at) ? 1 : -1)
                },
                {
                  key: "clientName",
                  label: tl("reports.client"),
                  sortable: true,
                  sortFunction: (a, b) =>
                    or(a.client.firstName).toLowerCase() > or(b.client.lastName).toLowerCase()
                      ? 1
                      : -1,
                  formatter: ({ client }) => (
                    <Typography>{clientFullNameSelector(client)}</Typography>
                  )
                },
                {
                  key: "status",
                  label: tl("reports.billStatus"),
                  formatter: ({ status }) => (
                    <Typography>
                      <BillingPackageStatusChip status={status} />
                    </Typography>
                  )
                },
                {
                  key: "discountAmount",
                  label: tl("reports.discountAmount"),
                  formatter: ({ discountAmount }) => (
                    <Typography>{rupeeDisplay(discountAmount || 0)}</Typography>
                  )
                },
                {
                  key: "dueAmount",
                  label: tl("reports.dueAmount"),
                  formatter: ({ dueAmount }) => (
                    <Typography>{rupeeDisplay(dueAmount || 0)}</Typography>
                  )
                },
                {
                  key: "paidAmount",
                  label: tl("reports.paidAmount"),
                  formatter: ({ paidAmount }) => (
                    <Typography>{rupeeDisplay(paidAmount || 0)}</Typography>
                  )
                },
                {
                  key: "totalAmount",
                  label: tl("reports.total"),
                  formatter: ({ totalAmount }) => (
                    <Typography>{totalAmount ? rupeeDisplay(totalAmount) : ""}</Typography>
                  )
                }
              ]}
            >
              <EmptyView>
                <Box textAlign="center" padding="50px">
                  No items to show.
                </Box>
              </EmptyView>
            </List>
          </Box>
        </Box>
      </Box>
    </Can>
  );
};

export default BillingPackageReport;
